/**
 * Ändern, ohne den anderen zu überfahren.
 *
 * Das Backoffice wird von mehreren Personen genutzt. Der Normalfall ist
 * harmlos — zwei Leute schauen dieselbe Liste an. Der teure Fall ist: beide
 * öffnen denselben Auftrag, einer trägt den Preis ein, der andere hatte den
 * Datensatz noch vom Vormittag im Browser und speichert seine Notiz. Ohne
 * Prüfung wäre der Preis danach wieder weg, und niemand würde es merken,
 * bis die Rechnung falsch ist.
 *
 * Deshalb schickt jede Änderung den `geaendert_am`-Wert mit, den sie gelesen
 * hat. Trifft er nicht mehr zu, ändert die Datenbank keine Zeile — und wir
 * sagen es ehrlich, statt still zu überschreiben. Das ist derselbe Gedanke
 * wie die Client-UUIDs in der App: lieber ein sichtbarer Konflikt als ein
 * unsichtbarer Datenverlust.
 *
 * Warum kein Datenbank-Trigger, der das erzwingt: Ein Trigger könnte den
 * Vorgang nur abbrechen, aber nicht sagen, WAS sich geändert hat. Die
 * Entscheidung, wie es weitergeht, gehört an den Bildschirm.
 */

import { supabase } from '../supabase';
import { alsKlarfehler, gleichzeitigGeaendert, type Klarfehler } from './fehler';

export class Speicherfehler extends Error {
  klar: Klarfehler;
  constructor(klar: Klarfehler) {
    super(klar.titel);
    this.klar = klar;
  }
}

/** Jede Tabelle, die geändert werden darf, führt diese beiden Spalten. */
export type MitStand = { id: string; geaendert_am: string };

type AendernAuftrag = {
  tabelle: string;
  /**
   * Die Spalte, die die Zeile bestimmt. Fast immer `id` — aber nicht überall:
   * `relationsvorlage_preis` hängt 1:1 an der Vorlage und hat als Schlüssel
   * `relationsvorlage_id`. Ein Parameter statt einer zweiten Kopie dieser
   * Funktion, damit die Standprüfung an EINER Stelle stimmen muss.
   */
  schluessel?: string;
  id: string;
  /** Der `geaendert_am`-Wert aus dem Datensatz, den der Bildschirm anzeigt. */
  gelesenerStand: string;
  werte: Record<string, unknown>;
  /** Wie der Datensatz in einer Meldung heißen soll, z. B. „Der Mitarbeiter". */
  was: string;
  /** Was nach einem Konflikt zu tun ist — siehe `gleichzeitigGeaendert()`. */
  danach?: string;
};

/**
 * Für Bildschirme, die nach einem Konflikt selbst auffrischen und die Eingabe
 * im Formular stehen lassen (H1.6).
 */
export const NEUER_STAND_GELADEN =
  'Der neue Stand ist geladen und deine Eingabe steht noch da — prüf, was jetzt gilt, und speichere noch einmal, falls deine Änderung gelten soll.';

export async function aendern<T extends { geaendert_am: string } = MitStand>(
  auftrag: AendernAuftrag,
): Promise<T> {
  const schluessel = auftrag.schluessel ?? 'id';
  const { data, error } = await supabase
    .from(auftrag.tabelle)
    .update(auftrag.werte)
    .eq(schluessel, auftrag.id)
    .eq('geaendert_am', auftrag.gelesenerStand)
    .select();

  if (error) throw new Speicherfehler(alsKlarfehler(error));
  if (!data || data.length === 0) return keineZeileGetroffen(auftrag);
  return data[0] as T;
}

/**
 * Löschen mit derselben Standprüfung wie `aendern()`.
 *
 * Ohne Stand räumte ein Löschen auch weg, was jemand anderes in der
 * Zwischenzeit eingetragen hat — man hätte etwas entfernt, das man nie
 * gesehen hat. `.select()` ist nicht Kosmetik: Ohne sie meldet PostgREST bei
 * null getroffenen Zeilen genauso Erfolg wie bei einer.
 */
export async function entfernen(
  auftrag: Omit<AendernAuftrag, 'werte'>,
): Promise<void> {
  const schluessel = auftrag.schluessel ?? 'id';
  const { data, error } = await supabase
    .from(auftrag.tabelle)
    .delete()
    .eq(schluessel, auftrag.id)
    .eq('geaendert_am', auftrag.gelesenerStand)
    .select(schluessel);

  if (error) throw new Speicherfehler(alsKlarfehler(error));
  if (!data || data.length === 0) return keineZeileGetroffen(auftrag);
}

/**
 * Keine Zeile getroffen. Zwei Möglichkeiten, und sie verlangen ganz
 * verschiedene Antworten — deshalb fragen wir nach, statt zu raten.
 *
 * Auch für Datenbankfunktionen mit Standprüfung, die bei veraltetem Stand
 * leer antworten (`mitarbeiter_rolle_status_setzen`).
 */
export async function keineZeileGetroffen(
  auftrag: Omit<AendernAuftrag, 'werte'>,
): Promise<never> {
  const schluessel = auftrag.schluessel ?? 'id';
  const { data: vorhanden, error } = await supabase
    .from(auftrag.tabelle)
    .select(schluessel)
    .eq(schluessel, auftrag.id)
    .maybeSingle();

  // Scheitert die Nachfrage selbst (Netz, Anmeldung), wissen wir nichts — und
  // „nicht mehr vorhanden" wäre eine falsche Erklärung, schlimmer als keine.
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  if (vorhanden) {
    throw new Speicherfehler(gleichzeitigGeaendert(auftrag.was, auftrag.danach));
  }

  throw new Speicherfehler({
    art: 'regel',
    titel: 'Nicht mehr vorhanden',
    text: `${auftrag.was} ist nicht mehr da — entweder gelöscht, oder dein Konto darf ihn nicht mehr sehen.`,
    original: 'kein Datensatz mit dieser Kennung sichtbar',
  });
}

/**
 * Anlegen. Braucht keine Standprüfung, aber dieselbe Fehlerübersetzung —
 * damit an keiner Stelle im Backoffice ein roher Datenbanktext auftaucht.
 */
export async function anlegen<T>(
  tabelle: string,
  werte: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase
    .from(tabelle)
    .insert(werte)
    .select()
    .single();

  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return data as T;
}

/** Eine Datenbankfunktion aufrufen, mit derselben Fehlerübersetzung. */
export async function aufrufen<T>(
  funktion: string,
  parameter: Record<string, unknown> = {},
): Promise<T> {
  const { data, error } = await supabase.rpc(funktion, parameter);
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return data as T;
}
